import Cookies from 'js-cookie';
import serialize from 'serialize-javascript';
import {
  selectCookiePreferences,
  selectSegmentCdnHost,
  selectSegmentWriteKey,
  selectIsInApp,
} from '../selectors/client';
import { COOKIE_PREFERENCES_COOKIE } from '../constants';
import { updateGoogleTagConsent } from '../lib/gtag';
import normalizeCampaignParams from '../lib/normalizeCampaignParams';

const NECESSARY_EVENTS = [
  'Resumed Playback',
  'Started Playback',
  'Paused Playback',
  'Served Ad Id',
  'Stopped Playback',
  'Updated Ad Playback Duration',
  'Updated Playback Duration',
  'Ad Playback Start Timeout Error',
  'Playback Start Timeout Error',
  'Playback Start Error',
  'Measured Time to Play v1',
];

const ADVERTISING_CATEGORIES = [
  'Advertising',
  'Tag Managers',
  'CRM',
  'Customer Success',
  'Deep Linking',
  'Email Marketing',
  'Helpdesk',
  'Livechat',
  'Performance Monitoring',
  'Personalization',
  'SMS & Push Notifications',
  'Security & Fraud',
];

const isBrazeDestination = destination => /^braze/i.test(destination.name || '');

const getDestinationPreferences = (destinations, preferences, isInApp) => {
  const destinationPreferences = {};

  Object.values(destinations).forEach(destination => {
    const category = destination.category;

    // In-app, Braze is covered by the app's adConsent, which maps to
    // marketingAndAnalytics, even though its Segment category is advertising.
    if (isInApp && isBrazeDestination(destination)) {
      destinationPreferences[destination.id] = preferences.marketingAndAnalytics;
      return;
    }

    if (destinationPreferences[destination.id] !== false) {
      if (ADVERTISING_CATEGORIES.includes(category)) {
        destinationPreferences[destination.id] = preferences.advertising;
      }
    }

    // Fallback to marketing
    if (!(destination.id in destinationPreferences)) {
      destinationPreferences[destination.id] = preferences.marketingAndAnalytics;
    }
  });

  return destinationPreferences;
};

async function fetchSegmentDestinations(cdnHost, writeKey) {
  const res = await fetch(`${cdnHost}/v1/projects/${writeKey}/integrations`);

  if (!res.ok) {
    throw new Error(
      `Failed to fetch integrations for write key ${writeKey}: HTTP ${res.status} ${res.statusText}`
    );
  }

  const destinations = await res.json();

  for (const destination of destinations) {
    if (destination.name === 'Fullstory') {
      destination.id = destination.name;
    } else {
      destination.id = destination.creationName;
    }
    delete destination.creationName;
  }

  return destinations;
}

async function loadSegment(store) {
  const state = store.getState();
  const segmentWriteKey = selectSegmentWriteKey(state);
  const segmentCdnHost = selectSegmentCdnHost(state);

  const destinations = await fetchSegmentDestinations(segmentCdnHost, segmentWriteKey);

  analytics.addSourceMiddleware(({ payload, next: nextAnalytics, integrations }) => {
    const currentState = store.getState();
    const cookiePreferences = selectCookiePreferences(currentState);
    const eventName = payload.obj.event;
    const shouldOverwritePreferencesAndAllowCookies = NECESSARY_EVENTS.includes(eventName);
    const allCookiesAllowed =
      shouldOverwritePreferencesAndAllowCookies ||
      Object.values(cookiePreferences).every(value => value);

    if (!allCookiesAllowed) {
      const destinationPreferences = getDestinationPreferences(
        destinations,
        cookiePreferences,
        selectIsInApp(currentState)
      );
      if (Object.values(destinationPreferences).every(value => !value)) {
        // don't send unnecessary events if all cookies are disabled
        return;
      }

      // otherwise modify integrations based on cookie preferences
      payload.obj.integrations = { ...integrations, ...destinationPreferences };
    }

    const { context } = payload.obj;
    if (context && context.campaign) {
      context.campaign = normalizeCampaignParams(context.campaign);
    }

    nextAnalytics(payload);
  });

  analytics.load(serialize(segmentWriteKey));
}

export default store => next => async action => {
  if (action.type === 'SAVE_COOKIES_CONSENT') {
    if (!selectIsInApp(store.getState()) && !Cookies.get(COOKIE_PREFERENCES_COOKIE)) {
      Cookies.set(COOKIE_PREFERENCES_COOKIE, action.preferences, {
        secure: process.env.IS_HEROKU === 'true' && !process.env.NO_HTTPS,
        expires: 365 * 100,
        domain: process.env.TOP_DOMAIN,
      });
    }

    updateGoogleTagConsent(action.preferences);

    if (window.analytics) {
      loadSegment(store);
    }
  }

  next(action);
};
